import { Box, ButtonBase, Card, CardContent, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import type { BillingSummary } from '../../lib/ops-api';
import { formatRupiah } from '../../lib/format';
import { useT } from '../../i18n';

// Most important first: what came in, what is late, what is still open.
const ORDER = ['PAID', 'OVERDUE', 'OPEN', 'DRAFT', 'VOID'] as const;
type Status = (typeof ORDER)[number];

const LABEL_KEY: Record<Status, string> = {
  PAID: 'rpt.statusPaid',
  OVERDUE: 'rpt.statusOverdue',
  OPEN: 'rpt.statusOpen',
  DRAFT: 'rpt.statusDraft',
  VOID: 'rpt.statusVoid',
};

/**
 * Invoices of the period as one bar split by status. Every segment and legend
 * entry opens the invoice list filtered to that status.
 */
export function StatusBreakdownBar({ summary, periodLabel }: { summary: BillingSummary; periodLabel: string }) {
  const t = useT();
  const theme = useTheme();
  const navigate = useNavigate();

  const color: Record<Status, string> = {
    PAID: theme.palette.success.main,
    OVERDUE: theme.palette.error.main,
    OPEN: theme.palette.warning.main,
    DRAFT: alpha(theme.palette.text.primary, 0.35),
    VOID: alpha(theme.palette.text.primary, 0.15),
  };
  const rows = ORDER.map((s) => ({ status: s, count: summary.byStatus[s] ?? 0, cents: summary.byStatusCents[s] ?? 0 }));
  const total = rows.reduce((n, r) => n + r.count, 0);
  const open = (s: Status) => navigate(`/invoices?status=${s}`);
  const tip = (r: (typeof rows)[number]) =>
    t('rpt.statusTip', { status: t(LABEL_KEY[r.status]), count: r.count, amount: formatRupiah(r.cents) });

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>{t('rpt.invoicesByStatus', { period: periodLabel })}</Typography>
          <Typography variant="caption" color="text.secondary">{t('rpt.statusTotal', { count: total })}</Typography>
        </Stack>

        {total === 0 ? (
          <Typography variant="body2" color="text.secondary">{t('rpt.statusNone')}</Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', gap: '2px' }}>
              {rows.filter((r) => r.count > 0).map((r) => (
                <Tooltip key={r.status} title={tip(r)} arrow>
                  <ButtonBase
                    aria-label={tip(r)}
                    onClick={() => open(r.status)}
                    sx={{
                      flexGrow: r.count,
                      flexBasis: 0,
                      minWidth: 6,
                      bgcolor: color[r.status],
                      transition: 'filter 150ms ease, transform 150ms ease',
                      '&:hover, &.Mui-focusVisible': { filter: 'brightness(1.12)', transform: 'scaleY(1.25)' },
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
            <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              {rows.map((r) => (
                <ButtonBase
                  key={r.status}
                  onClick={() => open(r.status)}
                  disabled={r.count === 0}
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    gap: 0.75,
                    opacity: r.count === 0 ? 0.5 : 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color[r.status] }} />
                  <Typography variant="body2">{t(LABEL_KEY[r.status])}</Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.count}</Typography>
                  {r.cents > 0 && (r.status === 'PAID' || r.status === 'OVERDUE' || r.status === 'OPEN') ? (
                    <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      · Rp {formatRupiah(r.cents)}
                    </Typography>
                  ) : null}
                </ButtonBase>
              ))}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}
